import { closestCenter, DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import React, { useState, useEffect, useCallback } from 'react'
import SmartSortableItem from './SmartSortableItem'

// Every list built on this container is a vertical one (`verticalListSortingStrategy`), so the dragged
// item never has a reason to leave its column. Locking the axis here keeps a sideways move of the
// mouse, a horizontal scroll adjustment or an autoscroll from carrying the item (and the page's
// scroll width with it) away to the side.
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 })

// With a DragOverlay the ghost can be born far from its placeholder: the Planner folds the tall
// blocks above the pressed one, so the list shrinks upward while the ghost stays under the pointer.
// `closestCenter` alone would then call the ghost "over" the nearest end of the list, the placeholder
// would jump to that end the moment the block is picked up, and a release before ever reaching the
// list would move the block there. Farther than one item height outside the list's span, the ghost
// is over its own slot; inside (or just past an end), the usual nearest-centre rule applies.
const closestCenterWithinList = (args) => {
  const { active, collisionRect, droppableRects, droppableContainers } = args
  const rects = Array.from(droppableRects.values())
  if (collisionRect && rects.length > 0) {
    const top = Math.min(...rects.map((rect) => rect.top))
    const bottom = Math.max(...rects.map((rect) => rect.bottom))
    const centre = collisionRect.top + collisionRect.height / 2
    if (centre < top - collisionRect.height || centre > bottom + collisionRect.height) {
      const own = droppableContainers.find((container) => container.id === active.id)
      return own ? [{ id: own.id, data: { droppableContainer: own, value: 0 } }] : []
    }
  }
  return closestCenter(args)
}

const SmartSortableContainer = ({
  itemList, // List of items that must be shown sortable with drag and drop
  itemId, // IMPORTANT: is the field or property in each item that will be used as id by the drag and drop control
  renderItem, // You must pass a custom component that will render each item of the list
  onSortChanged,
  renderChildren,
  disabled = false, // When true, disables drag and drop reordering
  // When true, `renderItem` receives `dragHandleProps` and must spread them on the one element that
  // starts a drag; the rest of the item is then inert. By default the whole item is the grip.
  dragHandle = false,
  // dnd-kit scrolls a scrollable ancestor when the pointer nears its edge. A list that is short while
  // dragging does not need it, and the page moving under the pointer reads as a glitch.
  autoScroll = true,
  // Field (or function) giving each item's position, used to sort `itemList` on every change. By
  // default the numeric `itemId` doubles as the position -- which forces consumers to renumber ids on
  // every drop and therefore to REMOUNT every item (the ids are the React keys). An item whose
  // identity must survive a reorder (a Gantt, a chart) names its position here and keeps its id.
  itemOrder = undefined,
  // After a drop dnd-kit focuses the drag handle again, and `focus()` scrolls a handle that is
  // partly off-screen into view: a wide header inside a horizontally scrolled page jumps sideways.
  restoreFocus = true,
  // Space/Enter on the focused grip start a keyboard drag. When the grip is a control with a meaning
  // of its own for those keys (an accordion header toggles), the consumer turns the sensor off.
  keyboardSensor = true,
  // When given, the dragged item is drawn by dnd-kit's DragOverlay from what this returns for the
  // active item, fixed to the pointer, while the item itself stays in the list as a placeholder.
  // The list may then change its layout freely during the drag without the ghost following it.
  // dnd-kit measures what this returns (its first child) at drag start, transform included, and
  // adds the pointer's movement on top: the returned element may carry its own offset, and dnd-kit
  // will look for the drop target exactly where the ghost is drawn.
  renderOverlay = undefined
}) => {
  const [items, setItems] = useState(itemList)
  const [activeId, setActiveId] = useState(null)
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8
    }
  })
  const keyboard = useSensor(KeyboardSensor)
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5
    }
  })
  const sensors = keyboardSensor ? [mouseSensor, keyboard, touchSensor] : [mouseSensor, touchSensor]

  const getItemId = useCallback((item) => {
    if (itemId instanceof Function) {
      return itemId(item)
    }
    return item[itemId]
  }, [itemId])

  const getItemOrder = useCallback((item) => {
    if (itemOrder === undefined) {
      return getItemId(item)
    }
    if (itemOrder instanceof Function) {
      return itemOrder(item)
    }
    return item[itemOrder]
  }, [itemOrder, getItemId])

  useEffect(() => {
    if (!itemList) return
    const sorted = [...itemList].sort((a, b) => getItemOrder(a) - getItemOrder(b))
    setItems(sorted)
  }, [itemList, getItemOrder])

  const onDragEnd = ({ active, over }) => {
    setActiveId(null)
    if (disabled) return
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => getItemId(item) === active.id)
      const newIndex = items.findIndex(item => getItemId(item) === over.id)

      const newItems = arrayMove(items, oldIndex, newIndex)
      setItems(newItems)
      if (onSortChanged && typeof onSortChanged === 'function') {
        onSortChanged(newItems)
      }
    }
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={renderOverlay ? closestCenterWithinList : closestCenter}
        modifiers={[restrictToVerticalAxis]}
        autoScroll={autoScroll}
        accessibility={{ restoreFocus }}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={items.map(item => getItemId(item))}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <div key={getItemId(item)}>
              <SmartSortableItem itemId={getItemId(item)} index={index} item={item} renderItem={renderItem} disabled={disabled} dragHandle={dragHandle} />
              {renderChildren && renderChildren(item)}
            </div>
          ))}
        </SortableContext>
        {renderOverlay && (
          // No drop animation: the placeholder already sits where the item lands, and an offset ghost
          // would be animated to a spot that offset away from it.
          <DragOverlay dropAnimation={null}>
            {activeId !== null && items.some((item) => getItemId(item) === activeId)
              ? renderOverlay(items.find((item) => getItemId(item) === activeId))
              : null}
          </DragOverlay>
        )}
      </DndContext>
    </>
  )
}

export default SmartSortableContainer
